import { useHistory } from './router-compat'
import { history } from '../redux/history'
import { NON_VALID_ENTRY_PATHS } from '../constants/oauth'
import { ROOT } from '../constants/routes'

/**
 * Read the current location's query string as a plain object.
 *
 * Synchronous on purpose: the app-init effect in LayoutHOC publishes
 * `window.Service` and `window.Logger` from this read, and an `await` there let a
 * lazily-rendered page's mount effect run first and read `window.Logger.log`
 * off `undefined` (SG-14610).
 *
 * @param {object} history - Router history.
 * @returns {object} The search params as key/value pairs.
 */
export const readHistoryParams = (history) => {
    const { location } = history
    const searchParams = new URLSearchParams(location.search)
    return Object.fromEntries(searchParams.entries())
}

/**
 * Promise-wrapped `readHistoryParams` for callers that chain on it.
 *
 * @param {object} history - Router history.
 * @returns {Promise<object>} Resolves with the search params as key/value pairs.
 */
export const getHistoryParams = (history) => Promise.resolve(readHistoryParams(history))

export const getHistoryFrom = (history) => {
    const { location } = history
    const { state } = location || {}
    const { from } = state || {}
    const { hash, pathname, search } = from || {}
    return {
        hash,
        pathname,
        search
    }
}

export const getHistoryFromUri = (history, ignore = {}) => {
    let uri = ''
    const { hash, pathname, search } = getHistoryFrom(history)
    if (!ignore.pathname) {
        uri = uri.concat(pathname || '/')
    }
    if (!ignore.search) {
        uri = uri.concat(search || '')
    }
    if (!ignore.hash) {
        uri = uri.concat(hash || '')
    }
    return uri
}

export const getEntryPageUrl = (history) => {
    const { location: { pathname, search, hash } } = history
    return { pathname, search, hash }
}

export const useCurrentPage = () => {
    const history = useHistory()
    const { location: { pathname } } = history
    switch (true) {
        case pathname.startsWith('/account'):
            return 'account'
        case pathname.startsWith('/connections'):
            return 'connections'
        case pathname.startsWith('/templates'):
            return 'templates'
        case pathname.startsWith('/workflows'):
            return 'workflows'
        default:
            return null
    }
}

export const setPath = (route, params) => {
    const paramsKeys = Object.keys(params || {})
    if (!paramsKeys.length) {
        return typeof route === 'string'
            ? route.replace(/[*|?]$/g, '')
            : route
    }
    let newRoute = route
    paramsKeys.forEach(p => {
        const regex = new RegExp(`:\\b${p}\\b`, 'g')
        newRoute = newRoute.replace(regex, params[p])
    })
    return newRoute.replace(/[*|?]$/g, '')
}

export const isPublicPath = (pathname = '') => {
    const publicPathList = [/\/(forms|dashboards)\/[\w|\d|-]{36}\/?/]
    try {
        return publicPathList.some(regExp => pathname.match(regExp))
    } catch {
        return false
    }
}

/**
 * Build the post-auto-sign-in landing URL from the current query string,
 * preserving only the `migrate` param (notably `?migrate=1` for the
 * cap-migration handoff) and stripping every other param, including the
 * sensitive one-time `userAuthToken`.
 *
 * @param {string} search - the current location.search (may include a leading '?').
 * @returns {string} a relative URL: '/' when `migrate` is absent, else '/?migrate=<value>'.
 */
export const buildAutoSignInLandingUrl = (search) => {
    const params = new URLSearchParams(search || '')
    const migrate = params.get('migrate')
    if (!migrate) return '/'
    const next = new URLSearchParams()
    next.set('migrate', migrate)
    return `/?${next.toString()}`
}

/**
 * Redirect the user to their entry page URL.
 *
 * @param {string} entryPageUrl - The entry page URL.
 * @param {Function} dispatch - The Redux dispatch function.
 */
export const redirectToEntryPage = (entryPageUrl, dispatch) => {
    try {
        if (!entryPageUrl || typeof entryPageUrl !== 'string') return
        const currentPathname = window.location.pathname
        const [, url] = entryPageUrl.split('sheetgo.com/')
        const [pathname, search] = url.split('?')
        const searchParams = new URLSearchParams(search || '')
        if (!pathname) {
            if (currentPathname === ROOT) return
            history.navigate({
                pathname: ROOT,
                search: decodeURIComponent(searchParams.toString())
            })
            return
        }
        const newPathname = !pathname.startsWith('/') ? `/${pathname}` : pathname
        if (NON_VALID_ENTRY_PATHS.some(p => p === newPathname)) {
            history.navigate({
                pathname: newPathname,
                search: decodeURIComponent(searchParams.toString())
            })
            return
        }
        if (currentPathname === newPathname) return
        history.navigate({
            pathname: newPathname,
            search: decodeURIComponent(searchParams.toString())
        })
    } catch { /** pass */ }
}

export default {
    redirectToEntryPage,
    buildAutoSignInLandingUrl,
    getHistoryFromUri,
    getHistoryParams,
    readHistoryParams,
    getEntryPageUrl,
    getHistoryFrom,
    useCurrentPage,
    isPublicPath,
    setPath
}
